import {
  AlertIcon,
  AuctionIcon,
  Button,
  CarIcon,
  CheckBox,
  CopyIcon,
  Headline,
  IdIcon,
  RegistrationPlate,
  ShowMoreIcon,
  Subtitle
} from '@kvdbil/components';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { getIdFromSlug } from '~/helpers/auction';
import { BASE_CARGO_DEV_URL, BASE_CARGO_URL } from '~/config/constants';
import { storeObjectById } from '../shared/selectors/storeObject';
import Divider from '../views/ObjectPage/components/Divider';
import { toggleShowDato } from '../shared/actions/kvdHelperActions';
import { showDato as showDatoSelector } from '../shared/selectors/kvdHelperSelectors';
import { useTranslation } from '~/Locale';
import { isClient } from '~/config/public/environment';
import { addSnack } from '../shared/actions/snackbarActions';
import { useCopyToClipboard } from '../shared/hooks/useClipToClipboard';

const Container = styled.div<{ isOpen: boolean }>`
  position: fixed;
  display: flex;
  align-items: center;
  transition: left 0.5s ease;
  left: ${({ isOpen }) => (isOpen ? '0px' : '-325px')};
  top: 50%;
  transform: translate(0, -50%);
  z-index: 500000;
`;

const Content = styled.div`
  width: 325px;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: #fff;
  border: 2px #1a2030 solid;
  border-left: none;
  box-shadow: 12px 12px 17px 1px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: 12px 12px 17px 1px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: 12px 12px 17px 1px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const Toggle = styled.button<{ isOpen: boolean }>`
  height: 2rem;
  width: 1.5rem;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: #fff;
  border: 2px #1a2030 solid;
  border-left: none;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 2px 0 0;
  font-size: 1.5rem;

  #arrow {
    transition: transform 0.5s ease;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(90deg)' : 'rotate(-90deg)')};
  }

  :hover {
    cursor: pointer;
  }
`;

const Header = styled.div`
  background-color: ${({ theme }) => theme.colors.primary.main};
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    height: 4rem;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem 0;
  gap: 1rem;
`;

const Info = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.2rem;
`;

const CopyButton = styled.button`
  background-color: transparent;
  border: none;
  height: 2rem;
  width: 2rem;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    cursor: pointer;
    background-color: #f0f0f0;
  }

  svg {
    pointer-events: none;
  }
`;

const Buttons = styled.div<{ isInfoShowing: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${({ isInfoShowing }) =>
    (isInfoShowing ? '2rem' : '1rem') + ' 0 1rem 0'};
  gap: 0.5rem;
`;

const CheckBoxArea = styled.div`
  justify-content: center;
  align-items: center;
  margin: 2rem 1rem 1rem 1rem;
`;

const encodeURL = (url: string) => url.replaceAll('&', '%26');

function KvdHelper({ location }: { location?: string }) {
  const dispatch = useDispatch();
  const showDato = useSelector(showDatoSelector);
  const { t } = useTranslation();
  const [, copyToClipboard] = useCopyToClipboard();

  const [isOpen, setIsOpen] = useState(false);
  const auctionId = getIdFromSlug(location?.split('/').pop());
  const auction = useSelector(storeObjectById(auctionId));

  const regPlate = auction?.processObject?.properties?.registrationPlate;

  const handleClick = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleCheckboxOnChange = useCallback(() => {
    dispatch(toggleShowDato());
  }, [dispatch]);

  const handleAddToClipboard = useCallback(
    async (value: string, message: string) => {
      await copyToClipboard(value);

      dispatch(
        addSnack({
          message,
          type: 'success',
          color: 'secondary'
        })
      );
    },
    [dispatch, copyToClipboard]
  );

  const encodedUrl = isClient ? encodeURL(window.location.href) : '';

  return (
    <Container isOpen={isOpen}>
      <Content>
        <Header>
          <img src="/images/kvdbil-logo-social.png" alt="logo" />
          <Headline style={{ marginRight: '0.5rem' }}>-</Headline>
          <Headline>Helper</Headline>
        </Header>
        {Boolean(auction) && (
          <InfoContainer>
            <Info>
              <IdIcon />
              <Subtitle>{auction?.processObject.id}</Subtitle>
              <CopyButton
                title="Copy process object id"
                onClick={() =>
                  handleAddToClipboard(
                    auction?.processObject.id ?? '',
                    t('Object number copied')
                  )
                }
              >
                <CopyIcon />
              </CopyButton>
            </Info>
            {regPlate && (
              <Info>
                <RegistrationPlate regNumber={regPlate} />
                <CopyButton
                  title="Copy registration number"
                  onClick={() =>
                    handleAddToClipboard(
                      regPlate,
                      t('Registration number copied')
                    )
                  }
                >
                  <CopyIcon />
                </CopyButton>
              </Info>
            )}
          </InfoContainer>
        )}
        <Divider />
        <Buttons isInfoShowing={Boolean(auction)}>
          {/* Might be added back later, but for now the Object app in Cargo is quite broken */}
          {/* <a
            href={`${
              process.env.NODE_ENV === 'production'
                ? BASE_CARGO_URL
                : BASE_CARGO_DEV_URL
            }/object-management/${auction?.processObject.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button prefixIcon={<CarIcon />} isDisabled={Boolean(!auction)}>
              {t('Object management')}
            </Button>
          </a> */}
          <a
            href={`fmp://k2.kvd.se/Kastor?script=fmp_slackLank&$koppling=objekt&$nyckel=${auction?.processObject.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              color="secondary"
              prefixIcon={<CarIcon />}
              isDisabled={Boolean(!auction)}
            >
              {t('Open in Kastor')}
            </Button>
          </a>
          <a
            href={`${
              process.env.NODE_ENV === 'production'
                ? BASE_CARGO_URL
                : BASE_CARGO_DEV_URL
            }/auction-management/active-auctions/${auction?.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              color="secondary"
              prefixIcon={<AuctionIcon />}
              isDisabled={Boolean(!auction)}
            >
              {t('Auction management')}
            </Button>
          </a>
          <a
            href={`https://kvarndammen.atlassian.net/servicedesk/customer/portal/1/group/24/create/42?summary=kvd.se:%20&description=Bug%20found%20here:%20${encodedUrl}%0A%0A`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button prefixIcon={<AlertIcon />} color="error">
              {t('Report bug/problem')}
            </Button>
          </a>
        </Buttons>
        <Divider />
        <CheckBoxArea>
          <CheckBox
            label={t('Edit Dato content')}
            checked={showDato}
            onChange={handleCheckboxOnChange}
          />
        </CheckBoxArea>
      </Content>
      <Toggle isOpen={isOpen} onClick={handleClick}>
        <ShowMoreIcon id="arrow" />
      </Toggle>
    </Container>
  );
}

export default KvdHelper;
